// ── Fassaden-Stammdaten (Zeichen-Schritt 5) ──────────────────────────
// Kleine Helfer rund um die gezeichneten Anwohner-Fassaden. Liegen hier
// statt im Zeichenwerkzeug, weil sowohl das Werkzeug
// (BuildingsPolyLineTool) als auch die Tabelle in der Sidebar
// (FacadeTable) sie brauchen.
//
// WICHTIG: `geometries.buildingPolylines` und `geometries.buildingHeights`
// werden index-parallel geführt — Fassade i hat Höhe i. Jede Stelle, die
// eine Fassade anlegt, löscht oder zurücknimmt, MUSS beide Arrays
// zusammen schreiben (dafür ist syncFacadeHeights da).
// ─────────────────────────────────────────────────────────────────────

// Bisheriger Festwert im Backend (create_geometries.py: `height = 8`).
// Bleibt der Vorgabewert, damit eine nicht angefasste Tabelle exakt so
// rechnet wie vor dieser Änderung.
export const DEFAULT_FACADE_HEIGHT = 8;

/**
 * Bringt das Höhen-Array auf die Länge der Fassadenliste:
 * fehlende Einträge bekommen die Vorgabehöhe, überzählige fallen weg.
 * Bereits eingetragene Werte bleiben unangetastet.
 */
export const syncFacadeHeights = (heights, count) => {
  const src = Array.isArray(heights) ? heights : [];
  return Array.from({ length: count }, (_, i) =>
    Number.isFinite(Number(src[i])) && Number(src[i]) > 0
      ? Number(src[i])
      : DEFAULT_FACADE_HEIGHT
  );
};

/**
 * Länge eines Polylinienzugs [[lon,lat], …] in Metern.
 *
 * Äquirektangulare Näherung statt Haversine: auf Sportplatz-Distanzen
 * (< 1 km) liegt der Fehler im Zentimeterbereich, und die Zahl ist hier
 * ohnehin nur eine Zuordnungshilfe („welche Linie ist Fassade 2?").
 */
export const facadeLengthMeters = (line) => {
  if (!Array.isArray(line) || line.length < 2) return 0;
  let total = 0;
  for (let i = 1; i < line.length; i++) {
    const [lon1, lat1] = line[i - 1];
    const [lon2, lat2] = line[i];
    const latMid = ((lat1 + lat2) / 2) * (Math.PI / 180);
    const dx = (lon2 - lon1) * Math.cos(latMid) * 111320;
    const dy = (lat2 - lat1) * 110540;
    total += Math.hypot(dx, dy);
  }
  return total;
};

/**
 * Mittelpunkt entlang der Linie (halbe Lauflänge) — dort sitzt das
 * Nummern-Label auf der Karte.
 */
export const facadeMidpoint = (line) => {
  if (!Array.isArray(line) || line.length === 0) return null;
  if (line.length === 1) return line[0];

  const half = facadeLengthMeters(line) / 2;
  let walked = 0;
  for (let i = 1; i < line.length; i++) {
    const seg = facadeLengthMeters([line[i - 1], line[i]]);
    if (walked + seg >= half) {
      const t = seg === 0 ? 0 : (half - walked) / seg;
      return [
        line[i - 1][0] + (line[i][0] - line[i - 1][0]) * t,
        line[i - 1][1] + (line[i][1] - line[i - 1][1]) * t,
      ];
    }
    walked += seg;
  }
  return line[line.length - 1];
};
